import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  SkeletonSpinnerCentered,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import type { Point } from 'geojson';
import { debounce, each, filter, isEmpty, keys } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { fetchGeotech } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Icon from '~/components2/Icon';
import Notify from '~/components2/Notify';
import { useIsSandboxOrg } from '~/hooks/useIsSandboxOrg';
import layerStore from '~/store2/layers';
import { setTabSyncedLayerVisible, useRestoreLayerOnUnmount } from '~/store2/tabLayerLocks';
import type { ProjectDetailsParams } from '..';
import { queryLayer } from './common';

interface OrderButtonProps {
  selectedFeatures: { [key: string]: GeoJSON.Feature };
}

function OrderButton({ selectedFeatures }: OrderButtonProps) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const isSandboxOrg = useIsSandboxOrg();
  const [open, setOpen] = useState(false);

  const selectedCount = keys(selectedFeatures).length;
  const buttonCTA = 'Order Reports';

  const freeCount = filter(selectedFeatures, (f) => f.properties!['Data_Owner'] === 'CivilGrid').length;
  const paidCount = selectedCount - freeCount;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={selectedCount === 0} data-testid="geotech-view-study-data-button">
          {`View Study Data ${selectedCount > 0 ? `(${selectedCount})` : ''}`}
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Order Studies"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="geotech-order-studies-dialog"
      >
        <DialogBody className="text-sm">
          <p>At this location, we have the following {selectedCount} report(s) available:</p>
          <table className="w-1/2">
            <thead className="text-left">
              <tr>
                <th>Report Type</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Free</td>
                <td>{freeCount}</td>
              </tr>
              <tr>
                <td>Paid</td>
                <td>{paidCount}</td>
              </tr>
            </tbody>
          </table>

          <p className="my-4">
            At each location, we have boring logs, and in some cases geotechnical studies available.{' '}
            <a
              href="/GeotechnicalStudies.zip"
              rel="noreferrer noopener"
              target="_blank"
              className="text-blue-50 underline hover:text-blue-60"
            >
              Here
            </a>{' '}
            is a sample of the type of data you can expect to receive.
          </p>
          <p className="my-4">
            Well Completion Reports are also available for download directly from the Geotechnical Tab.
          </p>
          <p className="my-4">
            If you click, <b>{buttonCTA}</b>, a CivilGrid representative will contact you to confirm boring log details,
            pricing, and delivery.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            disabled={isSandboxOrg()}
            data-testid="geotech-order-reports-button"
            onClick={async () => {
              // TODO place the API call
              const wellCompletionFeatures = filter(
                selectedFeatures,
                (feature) => feature.properties!.Type === 'Well Completion Reports',
              ).map(({ properties }) => properties);

              await fetchGeotech(projectId, keys(selectedFeatures), 'Geotechnical Stud', wellCompletionFeatures);

              setOpen(false);

              Notify.info({
                title: 'Geotechnical Report Ordered',
                message:
                  'A CivilGrid representative will contact you to confirm boring log details, pricing, and delivery.',
              });
            }}
          >
            {buttonCTA}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StudiesComponent() {
  const title = 'Geotechnical Studies';
  const GEOTECH_LAYER_ID = 'geotechnicalStudies';
  const fields = ['ID', 'Data_Source', 'Type', 'Operator'];

  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { projectDetailsMap } = useMap();
  const [isLoading, setLoading] = useState(true);
  const [queryResponse, setQueryResponse] = useState<GeoJSON.Feature[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<{
    [key: string]: GeoJSON.Feature;
  }>({});

  const { tilesConfig, visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;

  async function runQuery() {
    if (projectDetailsMap) {
      setLoading(true);
      const bounds = projectDetailsMap.getBounds()?.toArray();
      // @ts-expect-error
      const data = await queryLayer(GEOTECH_LAYER_ID, bounds, projectDetailsMap.getZoom());
      setQueryResponse(data);
      setSelectedFeatures({});
      setLoading(false);
    }
  }

  // On unmount, return the layer to picker-controlled visibility (the in-tab toggles above
  // must not clobber a layer the picker had on before entering the tab).
  useRestoreLayerOnUnmount(GEOTECH_LAYER_ID);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    runQuery();
    const delayedRunQuery = debounce(runQuery, 1_000);
    projectDetailsMap?.on('moveend', delayedRunQuery);
    return () => {
      delayedRunQuery.cancel();
      projectDetailsMap?.off('moveend', delayedRunQuery);
    };
  }, [projectDetailsMap?.loaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (queryResponse.length > 0) {
      if (queryResponse.length > 100) {
        return <div>Too many results to display. Zoom in or move map.</div>;
      }

      const layerConfig = tilesConfig?.[GEOTECH_LAYER_ID];
      const tableHeadings = fields.map((f) => (
        <th className="border p-1" key={f}>
          {layerConfig?.fields[f].alias}
        </th>
      ));
      const tableBody = queryResponse.map((r) => {
        return (
          <tr className="group/tr" key={r.id}>
            <td className="border">
              <Checkbox
                className="block"
                checked={!isEmpty(selectedFeatures[r.id!])}
                aria-label={`Select study ${r.properties?.ID ?? r.id}`}
                data-testid={`geotech-studies-select-checkbox-${r.id}`}
                onCheckedChange={(checked) => {
                  const id = r.id;
                  if (!checked) {
                    setSelectedFeatures((selectedFeatures) => {
                      const newSelectedFeature = { ...selectedFeatures };
                      delete newSelectedFeature[id!];
                      return newSelectedFeature;
                    });
                  } else {
                    setSelectedFeatures((selectedFeatures) => ({
                      ...selectedFeatures,
                      [id!]: r,
                    }));
                  }
                }}
              />
            </td>
            {fields.map((f, i) => (
              <td key={f} className={`relative max-w-[100px] overflow-hidden text-ellipsis border p-1 `}>
                {r.properties![f]}
                {i === 0 && r.geometry.type === 'Point' && (
                  <div
                    className="absolute left-0 top-0 my-2 hidden cursor-pointer rounded bg-accent-400 p-2 group-hover/tr:block"
                    onClick={() => {
                      setTabSyncedLayerVisible(GEOTECH_LAYER_ID, true);
                      projectDetailsMap?.flyTo({
                        center: (r.geometry as Point).coordinates as [number, number],
                        zoom: 22,
                      });
                    }}
                  >
                    View in map
                  </div>
                )}
              </td>
            ))}
            <td className="border text-center">
              {r.properties!['Data_Owner'] === 'CivilGrid' ? (
                <span className="flex flex-col items-center">
                  Free&nbsp;
                  {r.properties!['Link'] !== 'Not Available' && (
                    <a
                      href={r.properties!['Link']}
                      target="_blank"
                      data-testid={`geotech-study-download-${r.id}`}
                      aria-label={r.properties?.['ID'] ? `Download study ${r.properties['ID']}` : 'Download study'}
                      onClick={() => {
                        if (!r.properties) return;
                        capturePosthogEvent(POSTHOG_EVENTS.geotechnicalStudyOrdered, {
                          project_id: projectId,
                          study_id: r.properties['ID'],
                          data_source: r.properties['Data_Source'],
                          study_type: r.properties['Type'],
                          operator: r.properties['Operator'],
                          data_owner: r.properties['Data_Owner'],
                        });
                      }}
                    >
                      <Icon icon="download" />
                    </a>
                  )}
                </span>
              ) : (
                <span>Paid</span>
              )}
            </td>
          </tr>
        );
      });
      return (
        <div>
          <OrderButton selectedFeatures={selectedFeatures} />

          <table className="my-2 w-full table-auto border-collapse border text-xs">
            <thead>
              <tr className="border text-left text-xs">
                <th>
                  <Checkbox
                    className="block"
                    checked={keys(selectedFeatures).length === queryResponse.length}
                    aria-label="Select all studies"
                    data-testid="geotech-studies-select-all-checkbox"
                    onCheckedChange={(checked) => {
                      if (checked) {
                        const selectAll: { [key: string]: GeoJSON.Feature } = {};

                        each(queryResponse, (v) => (selectAll[v.id!] = v));
                        setSelectedFeatures(selectAll);
                      } else {
                        setSelectedFeatures({});
                      }
                    }}
                  />
                </th>
                {tableHeadings}
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>{tableBody}</tbody>
          </table>
        </div>
      );
    } else {
      return <div className="text-sm">No data in current map view - zoom out for more data.</div>;
    }
  }, [queryResponse, tilesConfig, selectedFeatures]);

  return (
    <div className="group w-full">
      <div className=" flex flex-row items-center space-x-2">
        <div className="w-4/5 py-2 text-base font-semibold">{title}</div>
        {queryResponse.length > 0 && (
          <div
            className="cursor-pointer text-xs text-blue-50 underline hover:text-blue-60"
            onClick={() => {
              setTabSyncedLayerVisible(GEOTECH_LAYER_ID, !visibleLayerIds.has(GEOTECH_LAYER_ID));
            }}
          >
            {visibleLayerIds.has(GEOTECH_LAYER_ID) ? 'Hide on Map' : 'Show on Map'}
          </div>
        )}
      </div>
      {isLoading ? <SkeletonSpinnerCentered /> : content}
    </div>
  );
}
